import { useState, useRef, useEffect, useCallback } from 'react';
import { Avatar, Typography, theme } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { usersApi } from '../../api/client';

const { Text } = Typography;

interface User {
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
}

interface Props {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    minRows?: number;
    onPressEnter?: () => void;
}

// Function to remove Polish characters
function removePolishChars(str: string): string {
    const map: Record<string, string> = {
        'ą': 'a', 'ć': 'c', 'ę': 'e', 'ł': 'l', 'ń': 'n',
        'ó': 'o', 'ś': 's', 'ź': 'z', 'ż': 'z',
        'Ą': 'A', 'Ć': 'C', 'Ę': 'E', 'Ł': 'L', 'Ń': 'N',
        'Ó': 'O', 'Ś': 'S', 'Ź': 'Z', 'Ż': 'Z',
    };
    return str.split('').map(char => map[char] || char).join('');
}

function generateLogin(firstName: string, lastName: string): string {
    const first = removePolishChars(firstName.toLowerCase().trim());
    const last = removePolishChars(lastName.toLowerCase().trim());
    return `${first}.${last}`;
}

// Parse value and render with highlighted mentions
function renderHighlightedContent(value: string): string {
    if (!value) return '';
    // Match @login[uuid] pattern and wrap in styled span
    return value.replace(
        /@([^\[]+)\[([^\]]+)\]/g,
        '<span class="mention-highlight" data-user-id="$2" contenteditable="false">@$1</span>'
    );
}

// Extract plain text with mention format from HTML
function extractTextFromHtml(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;

    let result = '';
    div.childNodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
            result += node.textContent;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as HTMLElement;
            if (el.classList.contains('mention-highlight')) {
                const userId = el.getAttribute('data-user-id');
                const login = el.textContent?.replace('@', '') || '';
                result += `@${login}[${userId}]`;
            } else {
                result += el.textContent;
            }
        }
    });

    return result;
}

/**
 * Rich text editor with @mention highlighting (Trello-style)
 * Shows mentions as highlighted blue chips in the input
 * Supports keyboard navigation in dropdown (↑↓ Enter)
 */
export default function RichMentionEditor({ value, onChange, placeholder, minRows = 2, onPressEnter }: Props) {
    const { token } = theme.useToken();
    const editorRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const skipNextSyncRef = useRef(false);
    const [mentionOpen, setMentionOpen] = useState(false);
    const [mentionSearch, setMentionSearch] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    // Fetch users for autocomplete
    const { data: usersData } = useQuery({
        queryKey: ['users-list'],
        queryFn: () => usersApi.getAll().then((r: any) => r.data),
    });
    const users: User[] = usersData || [];

    // Filter users based on search
    const filteredUsers = users.filter(user => {
        if (!mentionSearch) return true;
        const search = mentionSearch.toLowerCase();
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        const login = generateLogin(user.firstName, user.lastName);
        return fullName.includes(search) || login.includes(search);
    }).slice(0, 5);

    // Reset selected index when filtered users change
    useEffect(() => {
        setSelectedIndex(0);
    }, [filteredUsers.length, mentionSearch]);

    // Calculate dropdown position based on cursor
    const updateDropdownPosition = useCallback(() => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0 || !editorRef.current) return;

        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const editorRect = editorRef.current.getBoundingClientRect();

        setDropdownPosition({
            top: rect.bottom - editorRect.top + 4,
            left: rect.left - editorRect.left,
        });
    }, []);

    // Update editor content when value changes externally
    useEffect(() => {
        if (skipNextSyncRef.current) {
            skipNextSyncRef.current = false;
            return;
        }
        if (editorRef.current) {
            const currentText = extractTextFromHtml(editorRef.current.innerHTML);
            if (currentText !== value) {
                editorRef.current.innerHTML = renderHighlightedContent(value) || '<br>';
            }
        }
    }, [value]);

    // Handle input changes
    const handleInput = useCallback(() => {
        if (!editorRef.current) return;

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        // Get text content before cursor
        const range = selection.getRangeAt(0);
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(editorRef.current);
        preCaretRange.setEnd(range.endContainer, range.endOffset);
        const textBeforeCursor = preCaretRange.toString();

        const lastAtPos = textBeforeCursor.lastIndexOf('@');

        if (lastAtPos !== -1) {
            const textAfterAt = textBeforeCursor.slice(lastAtPos + 1);
            if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n') && textAfterAt.length <= 30) {
                setMentionOpen(true);
                setMentionSearch(textAfterAt);
                updateDropdownPosition();
            } else {
                setMentionOpen(false);
            }
        } else {
            setMentionOpen(false);
        }

        // Update the actual value
        const newValue = extractTextFromHtml(editorRef.current.innerHTML);
        if (newValue !== value) {
            onChange(newValue);
        }
    }, [value, onChange, updateDropdownPosition]);

    // Handle user selection
    const handleSelectUser = useCallback((user: User) => {
        if (!editorRef.current) return;

        const login = generateLogin(user.firstName, user.lastName);
        const currentValue = extractTextFromHtml(editorRef.current.innerHTML);

        // Find the last @ that is being typed
        const searchPattern = '@' + mentionSearch;
        let atPos = currentValue.lastIndexOf(searchPattern);

        if (atPos === -1) {
            for (let i = currentValue.length - 1; i >= 0; i--) {
                if (currentValue[i] === '@') {
                    const afterAt = currentValue.slice(i);
                    // Relaxed regex check
                    if (!afterAt.match(/^@[^\[]+\[[^\]]+\]/)) {
                        atPos = i;
                        break;
                    }
                }
            }
        }

        if (atPos === -1) return;

        const before = currentValue.slice(0, atPos);
        const after = currentValue.slice(atPos + 1 + mentionSearch.length);
        const newValue = before + `@${login}[${user.id}]` + ' ' + after;

        skipNextSyncRef.current = true;
        onChange(newValue);

        // Manually rebuild DOM
        editorRef.current.innerHTML = renderHighlightedContent(newValue);

        // Place cursor at end
        const lastChild = editorRef.current.lastChild;
        let cursorNode: Node;

        if (!lastChild || lastChild.nodeType !== Node.TEXT_NODE || (lastChild.textContent || '').trim() === '') {
            cursorNode = document.createTextNode('\u00A0');
            editorRef.current.appendChild(cursorNode);
        } else {
            cursorNode = lastChild;
        }

        editorRef.current.focus();
        const newRange = document.createRange();
        const sel = window.getSelection();

        if (cursorNode.nodeType === Node.TEXT_NODE) {
            const len = (cursorNode.textContent || '').length;
            newRange.setStart(cursorNode, len > 0 ? len : 0);
        } else {
            newRange.setStartAfter(cursorNode);
        }
        newRange.collapse(true);
        sel?.removeAllRanges();
        sel?.addRange(newRange);

        setMentionOpen(false);
        setMentionSearch('');
        setSelectedIndex(0);
    }, [mentionSearch, onChange]);

    // Handle keyboard navigation
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (mentionOpen && filteredUsers.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIndex(prev => (prev + 1) % filteredUsers.length);
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex(prev => (prev - 1 + filteredUsers.length) % filteredUsers.length);
                return;
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                handleSelectUser(filteredUsers[selectedIndex]);
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                setMentionOpen(false);
                return;
            }
        }

        if (e.key === 'Enter' && e.ctrlKey && onPressEnter) {
            e.preventDefault();
            onPressEnter();
        }

        if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && minRows === 1) {
            e.preventDefault();
        }
    }, [mentionOpen, filteredUsers, selectedIndex, handleSelectUser, onPressEnter, minRows]);

    const handleBlur = () => {
        setTimeout(() => setMentionOpen(false), 200);
    };

    const handleFocus = () => {
        if (editorRef.current && editorRef.current.innerHTML === '<br>') {
            editorRef.current.innerHTML = '';
        }
    };

    return (
        <>
            <style>{`
                .rich-mention-editor {
                    position: relative;
                    border: 1px solid ${token.colorBorder};
                    border-radius: 6px;
                    padding: 8px 12px;
                    min-height: ${minRows * 24 + 16}px;
                    background: ${token.colorBgContainer};
                    color: ${token.colorText};
                    cursor: text;
                    outline: none;
                    font-size: 14px;
                    line-height: 1.5715;
                    transition: border-color 0.3s, box-shadow 0.3s, background 0.3s;
                }
                .rich-mention-editor:focus {
                    border-color: ${token.colorPrimary};
                    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
                }
                .rich-mention-editor:empty::before {
                    content: attr(data-placeholder);
                    color: ${token.colorTextPlaceholder};
                    pointer-events: none;
                }
                .rich-mention-editor .mention-highlight {
                    color: #1890ff;
                    background-color: rgba(24, 144, 255, 0.15);
                    padding: 1px 4px;
                    border-radius: 3px;
                    font-weight: 500;
                    display: inline;
                    margin: 0 1px;
                }
                .mention-dropdown {
                    position: absolute;
                    z-index: 1050;
                    background: ${token.colorBgElevated};
                    border: 1px solid ${token.colorBorderSecondary};
                    border-radius: 8px;
                    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
                    min-width: 260px;
                    max-width: 320px;
                    overflow: hidden;
                }
                .mention-dropdown-item {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 10px 14px;
                    cursor: pointer;
                    transition: background 0.15s;
                }
                .mention-dropdown-item:hover,
                .mention-dropdown-item.selected {
                    background: ${token.colorFillAlter};
                }
                .mention-dropdown-item .user-info {
                    display: flex;
                    flex-direction: column;
                    min-width: 0;
                }
                .mention-dropdown-item .user-name {
                    color: ${token.colorText};
                    font-weight: 500;
                    font-size: 14px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .mention-dropdown-item .user-login {
                    color: ${token.colorTextSecondary};
                    font-size: 12px;
                }
                .mention-dropdown-empty {
                    padding: 16px;
                    text-align: center;
                    color: ${token.colorTextSecondary};
                    font-size: 13px;
                }
            `}</style>
            <div style={{ position: 'relative' }}>
                <div
                    ref={editorRef}
                    className="rich-mention-editor"
                    contentEditable
                    data-placeholder={placeholder}
                    onInput={handleInput}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    suppressContentEditableWarning
                />
                {mentionOpen && (
                    <div
                        ref={dropdownRef}
                        className="mention-dropdown"
                        style={{
                            top: dropdownPosition.top,
                            left: Math.max(0, dropdownPosition.left),
                        }}
                    >
                        {filteredUsers.length === 0 ? (
                            <div className="mention-dropdown-empty">
                                <Text style={{ color: 'rgba(255,255,255,0.5)' }}>Brak użytkowników</Text>
                            </div>
                        ) : (
                            filteredUsers.map((user, index) => (
                                <div
                                    key={user.id}
                                    className={`mention-dropdown-item ${index === selectedIndex ? 'selected' : ''}`}
                                    onClick={() => handleSelectUser(user)}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onMouseEnter={() => setSelectedIndex(index)}
                                >
                                    <Avatar
                                        size={32}
                                        style={{
                                            backgroundColor: '#f5a623',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {user.firstName[0]}
                                    </Avatar>
                                    <div className="user-info">
                                        <span className="user-name">
                                            {user.firstName} {user.lastName}
                                        </span>
                                        <span className="user-login">
                                            @{generateLogin(user.firstName, user.lastName)}
                                        </span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
        </>
    );
}
